import axiosInstance from '../api/axiosConfig';
import { getApiBaseUrl } from '../baseUrl';

// Interface para recuperação de senha
export interface RecuperarSenhaRequest {
  email: string;
}

export interface RecuperarSenhaResponse {
  success: boolean;
  message: string;
}

export interface CandidatoAprovadoContratado {
  id: number;
  candidatoId: number;
  candidatoNome: string;
  candidatoEmail: string;
  candidatoTelefone: string;
  vagaNome: string;
  cargo: string;
  empresaId: number;
  empresaNome: string;
  empresaEmail: string;
  empresaTelefone: string;
  dataAprovacao: string;
  dataContratacao?: string;
  status: 'APROVADO' | 'CONTRATADO';
  salario?: number;
  observacoes?: string;
}

interface CandidaturaBackendGestao {
  id: number;
  ativa: string;
  data_aplicacao: string;
  id_candidato: {
    id: number;
    nome: string;
    email: string;
    telefone: string;
    status?: 'ativo' | 'inativo';
  };
  id_vaga: {
    id: number;
    nome_vaga: string;
    cargo: string;
    descricao_vaga: string;
    salario: number;
    id_empresa: {
      id: number;
      nome: string;
      cnpj: string;
      email: string;
      telefone: string;
      endereco: string;
      ativo: string;
    };
  };
  status: string;
  data_aprovacao?: string;
  data_contratacao?: string;
  observacoes?: string;
}

// Mapear dados do backend para o formato da gestão da prefeitura
const mapearCandidatoGestao = (candidaturaBackend: CandidaturaBackendGestao): CandidatoAprovadoContratado => {
  return {
    id: candidaturaBackend.id,
    candidatoId: candidaturaBackend.id_candidato.id,
    candidatoNome: candidaturaBackend.id_candidato.nome,
    candidatoEmail: candidaturaBackend.id_candidato.email,
    candidatoTelefone: candidaturaBackend.id_candidato.telefone,
    vagaNome: candidaturaBackend.id_vaga.nome_vaga,
    cargo: candidaturaBackend.id_vaga.cargo,
    empresaId: candidaturaBackend.id_vaga.id_empresa?.id || 0,
    empresaNome: candidaturaBackend.id_vaga.id_empresa?.nome || 'N/A',
    empresaEmail: candidaturaBackend.id_vaga.id_empresa?.email || 'N/A',
    empresaTelefone: candidaturaBackend.id_vaga.id_empresa?.telefone || 'N/A',
    dataAprovacao: candidaturaBackend.data_aprovacao || candidaturaBackend.data_aplicacao,
    dataContratacao: candidaturaBackend.data_contratacao,
    status: candidaturaBackend.status as 'APROVADO' | 'CONTRATADO',
    salario: candidaturaBackend.id_vaga.salario,
    observacoes: candidaturaBackend.observacoes
  };
};

// Buscar todos os candidatos aprovados por todas as empresas
export const buscarCandidatosAprovados = async (): Promise<CandidatoAprovadoContratado[]> => {
  const apiUrl = getApiBaseUrl();
  
  try {
    // Primeiro buscar todas as empresas ativas
    const empresasResponse = await axiosInstance.get(`${apiUrl}/empresas`, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    const empresas = Array.isArray(empresasResponse.data) 
      ? empresasResponse.data 
      : (empresasResponse.data?.data || []);
    
    const empresasAtivas = empresas.filter((empresa: any) => empresa.ativo === 'S');
    
    // Buscar candidatos aprovados de cada empresa
    const promessasAprovados = empresasAtivas.map(async (empresa: any) => {
      try {
        const response = await axiosInstance.get(`${apiUrl}/candidaturas/empresa/${empresa.id}/aprovados`, {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        const aprovados = Array.isArray(response.data) ? response.data : [];
        return aprovados.map((candidatura: any) => ({
          ...candidatura,
          status: 'APROVADO',
          id_vaga: {
            ...candidatura.id_vaga,
            id_empresa: empresa
          }
        }));
      } catch (error) {
        return [];
      }
    });
    
    const resultadosAprovados = await Promise.all(promessasAprovados);
    const todosAprovados = resultadosAprovados.flat();
    
    // Remover filtragem por status - dados da API já vêm filtrados
    const candidaturasAtivas = todosAprovados;
    
    
    return candidaturasAtivas.map(mapearCandidatoGestao);
  } catch (error) {
    console.error('Erro ao buscar candidatos aprovados:', error);
    return [];
  }
};

// Buscar todos os candidatos contratados por todas as empresas
export const buscarCandidatosContratados = async (): Promise<CandidatoAprovadoContratado[]> => {
  const apiUrl = getApiBaseUrl();
  
  try {
    // Buscar todas as empresas ativas
    const empresasResponse = await axiosInstance.get(`${apiUrl}/empresas`, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    const empresas = Array.isArray(empresasResponse.data) 
      ? empresasResponse.data 
      : (empresasResponse.data?.data || []);
    
    const empresasAtivas = empresas.filter((empresa: any) => empresa.ativo === 'S');
    
    // Buscar candidatos contratados de todas as empresas em paralelo
    const promessasContratados = empresasAtivas.map(async (empresa: any) => {
      try {
        const response = await axiosInstance.get(`${apiUrl}/candidaturas/empresa/${empresa.id}/contratados`, {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        const contratados = Array.isArray(response.data) ? response.data : [];
        
        return contratados.map((candidato: any) => ({
          id: candidato.idCandidatura,
          candidatoId: candidato.idCandidato,
          candidatoNome: candidato.nomeCandidato || 'Nome não informado',
          candidatoEmail: candidato.emailCandidato || '',
          candidatoTelefone: candidato.telefoneCandidato || '',
          vagaNome: candidato.nomeVaga || 'Vaga não informada',
          cargo: candidato.nomeVaga || 'Cargo não informado', // Usando nomeVaga como cargo temporariamente
          empresaId: empresa.id,
          empresaNome: empresa.nome,
          empresaEmail: empresa.email,
          empresaTelefone: empresa.telefone,
          dataAprovacao: candidato.dataAprovacao,
          dataContratacao: candidato.dataAprovacao, // Usando data de aprovação como fallback
          status: 'CONTRATADO' as 'CONTRATADO',
          salario: candidato.salarioVaga ? Number(candidato.salarioVaga) : undefined,
          observacoes: candidato.anotacoes
        }));

      } catch (error: any) {
        console.warn(`Erro ao buscar candidatos contratados da empresa ${empresa.id}:`, error?.message);
        return [];
      }
    });

    const resultados = await Promise.allSettled(promessasContratados);
    const todosContratados = resultados
      .filter(result => result.status === 'fulfilled')
      .map(result => (result as PromiseFulfilledResult<any>).value)
      .flat();
    
    return todosContratados;
  } catch (error) {
    console.error('Erro ao buscar candidatos contratados:', error);
    return [];
  }
};

// Buscar estatísticas gerais para a prefeitura
export const buscarEstatisticasGerais = async () => {
  const apiUrl = getApiBaseUrl();
  
  try {
    // Fazer todas as chamadas em paralelo para melhor performance
    const [candidatosResponse, empresasResponse, candidaturasResponse, contratados] = await Promise.all([
      // 1. CANDIDATOS: Todos os usuários cadastrados
      axiosInstance.get(`${apiUrl}/candidatos`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      }),
      
      // 2. EMPRESAS: Todas as empresas
      axiosInstance.get(`${apiUrl}/empresas`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      }),
      
      // 3. CANDIDATURAS: Contagem total de candidaturas ativas
      axiosInstance.get(`${apiUrl}/candidaturas/ativas/count`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      }),
      
      // 4. CONTRATADOS: Candidatos contratados
      buscarCandidatosContratados()
    ]);
    
    const candidatos = Array.isArray(candidatosResponse.data)
      ? candidatosResponse.data
      : (candidatosResponse.data?.data || []);
    
    const todasEmpresas = Array.isArray(empresasResponse.data) 
      ? empresasResponse.data 
      : (empresasResponse.data?.data || []);
    
    // Contar candidaturas ativas
    const totalCandidaturas = typeof candidaturasResponse.data === 'number'
      ? candidaturasResponse.data
      : (candidaturasResponse.data?.data || 0);
    
    const totalContratados = contratados.length;
    const totalCandidatos = candidatos.length;
    
    // 5. EMPRESAS PARTICIPANTES: Empresas ativas que têm vagas com candidaturas ativas
    const empresasAtivas = todasEmpresas.filter((empresa: any) => empresa.ativo === 'S');
    
    // Buscar candidaturas de todas as empresas ativas em paralelo
    const promessasCandidaturas = empresasAtivas.map(async (empresa: any) => {
      try {
        // Buscar candidaturas pendentes da empresa
        let candidaturasPendentes = [];
        try {
          const candidaturasResp = await axiosInstance.get(`${apiUrl}/candidaturas/empresa/${empresa.id}/pendentes`, {
            timeout: 5000,
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
          });
          candidaturasPendentes = Array.isArray(candidaturasResp.data) ? candidaturasResp.data : [];
        } catch (error) {
          console.warn(`Erro ao buscar candidaturas pendentes da empresa ${empresa.id}:`, error);
        }
        
        // Buscar candidaturas aprovadas da empresa
        let candidaturasAprovadas = [];
        try {
          const aprovadasResp = await axiosInstance.get(`${apiUrl}/candidaturas/empresa/${empresa.id}/aprovados`, {
            timeout: 5000,
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
          });
          candidaturasAprovadas = Array.isArray(aprovadasResp.data) ? aprovadasResp.data : [];
        } catch (error) {
          console.warn(`Erro ao buscar candidaturas aprovadas da empresa ${empresa.id}:`, error);
        }
        
        // Buscar candidaturas contratadas da empresa
        let candidaturasContratadas = [];
        try {
          const contratadasResp = await axiosInstance.get(`${apiUrl}/candidaturas/empresa/${empresa.id}/contratados`, {
            timeout: 5000,
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
          });
          candidaturasContratadas = Array.isArray(contratadasResp.data) ? contratadasResp.data : [];
        } catch (error) {
          console.warn(`Erro ao buscar candidaturas contratadas da empresa ${empresa.id}:`, error);
        }
        
        const totalCandidaturas = candidaturasPendentes.length + candidaturasAprovadas.length + candidaturasContratadas.length;
        
        return {
          empresaId: empresa.id,
          empresaNome: empresa.nome,
          temCandidaturasAtivas: totalCandidaturas > 0
        };
      } catch (error) {
        console.warn(`Erro ao buscar candidaturas da empresa ${empresa.id}:`, error);
        return {
          empresaId: empresa.id,
          empresaNome: empresa.nome,
          temCandidaturasAtivas: false
        };
      }
    });
    
    const resultadosCandidaturas = await Promise.allSettled(promessasCandidaturas);
    const empresasComCandidaturasAtivas = resultadosCandidaturas
      .filter(result => result.status === 'fulfilled')
      .map(result => (result as PromiseFulfilledResult<any>).value)
      .filter(resultado => resultado.temCandidaturasAtivas);
    
    const empresasParticipantes = empresasComCandidaturasAtivas.length;
    
    return {
      totalContratados,
      totalCandidaturas,
      empresasParticipantes,
      candidatosProcessados: totalCandidatos
    };
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error);
    return {
      totalContratados: 0,
      totalCandidaturas: 0,
      empresasParticipantes: 0,
      candidatosProcessados: 0
    };
  }
};

// Recuperar senha da prefeitura
export const recuperarSenhaPrefeitura = async (email: string): Promise<RecuperarSenhaResponse> => {
  const apiUrl = getApiBaseUrl();

  try {
    const response = await axiosInstance.post(`${apiUrl}/prefeituras/recuperar-senha`, { email });

    return {
      success: response.data.success || true,
      message: response.data.message || 'Nova senha enviada para o email cadastrado'
    };
  } catch (error: any) {
    console.error('Erro ao recuperar senha:', error);

    if (error.response?.data?.message) {
      return {
        success: false,
        message: error.response.data.message
      };
    }

    return {
      success: false,
      message: 'Erro ao processar recuperação de senha'
    };
  }
};